import { useCallback, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import cx from 'classnames';
import { GridYValue, GridYUnit } from '../../constants';
import { getDragCanvasHeight } from '../../utility/canvasLayout';

// How close to the top or bottom edge of the window the pointer has to come
// before the page starts scrolling itself during a drag, and how far it scrolls
// per frame once the pointer is right against the edge.
const AUTO_SCROLL_EDGE = 100;
const AUTO_SCROLL_MAX_SPEED = 25;

export const getAutoScrollAmount = (clientY, viewportHeight = window.innerHeight) => {
  if (clientY < AUTO_SCROLL_EDGE) {
    return -AUTO_SCROLL_MAX_SPEED * (1 - clientY / AUTO_SCROLL_EDGE);
  }

  const distanceFromBottom = viewportHeight - clientY;
  if (distanceFromBottom < AUTO_SCROLL_EDGE) {
    return AUTO_SCROLL_MAX_SPEED * (1 - distanceFromBottom / AUTO_SCROLL_EDGE);
  }

  return 0;
};

// Where the dragged field has moved to: how far the pointer has travelled since
// the drag started, plus how far the page has scrolled underneath it in the
// meantime, since the pointer offsets react-dnd reports are viewport-relative.
export const getDraggedPosition = (item, difference, scrollDelta) => {
  let top = Math.floor((item.positionValues.top * GridYValue + difference.y + scrollDelta) / GridYValue);
  top = top <= 0 ? 0 : top;

  let left = Math.floor((((item.positionValues.left / 100) * item.areaWidth + difference.x) / item.areaWidth) * 100);

  if (left <= 0) {
    left = 0;
  } else if (left > 100 - item.positionValues.width) {
    left = 100 - item.positionValues.width;
  }

  return {
    top,
    left,
    width: item.positionValues.width,
    height: item.positionValues.height,
    startWidth: item.positionValues.startWidth,
    startHeight: item.positionValues.startHeight,
    innerElemHeight: item.positionValues.innerElemHeight,
    startInnerElemHeight: item.positionValues.startInnerElemHeight,
  };
};

export const DropArea = ({ onDragExtend, ...props }) => {
  // The last thing the pointer told us, kept in a ref so the scroll loop below
  // can keep moving the field while the pointer itself is standing still
  // against the edge of the window.
  const dragRef = useRef(null);
  const frameRef = useRef(null);

  const applyPosition = useCallback(
    (drag) => {
      if (!drag?.item?.setPosition || !drag.difference) {
        return;
      }

      const { item, difference } = drag;
      const position = getDraggedPosition(item, difference, window.scrollY - item.dragStartScrollY);

      item.newPosition = position;
      item.setPosition(position);
      // Keep canvas under the field as it travels down, or the drop target ends
      // and the drag stops being tracked half way.
      onDragExtend?.(getDragCanvasHeight(position));
    },
    [onDragExtend],
  );

  const [{ canDrop }, drop] = useDrop(
    () => ({
      accept: 'fromElement',
      drop: () => ({ name: 'AddNew' }),
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
      }),
      hover: (item, monitor) => {
        if (item.dragStartScrollY === undefined) {
          item.dragStartScrollY = window.scrollY;
        }

        const clientOffset = monitor.getClientOffset();

        dragRef.current = {
          item,
          difference: monitor.getDifferenceFromInitialOffset(),
          clientY: clientOffset ? clientOffset.y : null,
        };

        applyPosition(dragRef.current);
      },
    }),
    [applyPosition],
  );

  // `canDrop` is true for as long as a field is being dragged, whether or not it
  // is over the canvas, so it doubles as "a drag is running". While one is, run
  // the auto-scroll on animation frames rather than off drag events: the browser
  // reports those only now and then, and never at all once the pointer stops at
  // the edge — which is exactly when the page has to keep scrolling.
  useEffect(() => {
    if (!canDrop) {
      dragRef.current = null;
      // Give the headroom back; the dropped field is part of the canvas height now.
      onDragExtend?.(0);
      return undefined;
    }

    const step = () => {
      frameRef.current = requestAnimationFrame(step);

      const drag = dragRef.current;
      if (!drag || typeof drag.clientY !== 'number') {
        return;
      }

      const amount = getAutoScrollAmount(drag.clientY);
      if (!amount) {
        return;
      }

      const scrollBefore = window.scrollY;
      window.scrollBy(0, amount);

      if (window.scrollY !== scrollBefore) {
        // The pointer has not moved, but the page under it has, so the field has.
        applyPosition(drag);
      }
    };

    frameRef.current = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frameRef.current);
  }, [canDrop, applyPosition, onDragExtend]);

  return (
    <div
      ref={drop}
      className={cx('drop-area', { 'drop-area-active': canDrop })}
      data-testid="DropArea"
      style={{ height: `${props.formAreaHeight - 100}${GridYUnit}` }}
    >
      {props.children}
    </div>
  );
};
